'use client';
import React, { useState, ChangeEvent } from 'react';

interface ExamData {
  examType: string;
  A1: number;
  A2: number;
  B1: number;
  B2: number;
  C1: number;
  C2: number;
}

interface Exam {
  id: string;
  name: string;
  price: string;
}

interface LangCertProps {
  customExams?: Exam[] | null;
  customExamData?: ExamData[] | null;
  customBackgroundColor?: string | null;
  customTextColor?: string | null;
  examTypeText?: string;
  buttonText?: string;
  formText?: {
    title?: string;
    selectedLabel?: string;
    levelLabel?: string;
    nameLabel?: string;
    namePlaceholder?: string;
    phoneLabel?: string;
    emailLabel?: string;
    preferredCommunicationLabel?: string;
    preferredCommunicationPlaceholder?: string;
    preferredCommunicationOther?: string;
    errorPrefix?: string;
    successMessage?: string;
    cancelButton?: string;
    confirmButton?: string;
    processingText?: string;
  };
}

const ExamBookingTable: React.FC<LangCertProps> = ({
  customExams = null,
  customExamData = null,
  customBackgroundColor = null,
  customTextColor = null,
  examTypeText = 'Exam Type',
  buttonText = 'Book',
  formText,
}) => {
  const [selectedExam, setSelectedExam] = useState('written'); // Default to legacy format
  const [selectedLevel, setSelectedLevel] = useState('B2'); // Selected level
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredCommunicator: '',
  });

  // Default exam data structure
  const defaultExamData: ExamData[] = [
    {
      examType: 'Written',
      A1: 53,
      A2: 60,
      B1: 60,
      B2: 72,
      C1: 85,
      C2: 88,
    },
    {
      examType: 'Spoken',
      A1: 53,
      A2: 60,
      B1: 60,
      B2: 72,
      C1: 85,
      C2: 88,
    },
    {
      examType: 'Written and Spoken (4-skills exam)',
      A1: 106,
      A2: 120,
      B1: 120,
      B2: 144,
      C1: 170,
      C2: 176,
    },
    {
      examType: 'Written + Take2 re-sit exam option',
      A1: 75,
      A2: 82,
      B1: 82,
      B2: 94,
      C1: 107,
      C2: 110,
    },
    {
      examType: 'Spoken + Take2 re-sit exam option',
      A1: 75,
      A2: 82,
      B1: 82,
      B2: 94,
      C1: 107,
      C2: 110,
    },
    {
      examType: 'Written and Spoken (4-skills exam) + Take2 re-sit exam option',
      A1: 150,
      A2: 164,
      B1: 164,
      B2: 188,
      C1: 214,
      C2: 220,
    },
  ];

  // Use custom exam data if provided, otherwise use defaults
  const examData = customExamData || defaultExamData;
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  // Legacy support for simple exam structure
  const exams = customExams || [
    { id: 'written', name: 'Written', price: '€72.00' },
    { id: 'spoken', name: 'Spoken', price: '€72.00' },
    { id: 'both', name: 'Written and Spoken (4-skills exam)', price: '€144.00' },
  ];

  // Get current price based on selection
  const getCurrentPrice = () => {
    if (customExamData && customExamData.length > 0) {
      // New mode with levels
      const examIndex = parseInt(selectedExam);
      const exam = examData[examIndex];
      if (exam && selectedLevel) {
        const price = exam[selectedLevel as keyof ExamData];
        return `€${price}.00`;
      }
      return '€0.00';
    } else {
      // Legacy mode
      const exam = exams.find(e => e.id === selectedExam);
      return exam?.price || '€0.00';
    }
  };

  // Get current exam name
  const getCurrentExamName = () => {
    if (customExamData && customExamData.length > 0) {
      // New mode with levels
      const examIndex = parseInt(selectedExam);
      return examData[examIndex]?.examType || '';
    } else {
      // Legacy mode
      const exam = exams.find(e => e.id === selectedExam);
      return exam?.name || '';
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const resolvedFormText = {
    title: formText?.title || 'Book Your Exam',
    selectedLabel: formText?.selectedLabel || 'Selected',
    levelLabel: formText?.levelLabel || 'Level',
    nameLabel: formText?.nameLabel || 'Name',
    namePlaceholder: formText?.namePlaceholder || 'Your full name',
    phoneLabel: formText?.phoneLabel || 'Phone Number',
    phonePlaceholder: '+380 XX XXX XX XX',
    emailLabel: formText?.emailLabel || 'Email',
    emailPlaceholder: 'your-email@example.com',
    preferredCommunicationLabel: formText?.preferredCommunicationLabel || 'Preferred Communication',
    preferredCommunicationPlaceholder:
      formText?.preferredCommunicationPlaceholder || 'Choose messenger',
    preferredCommunicationOther: formText?.preferredCommunicationOther || 'Other',
    errorPrefix: formText?.errorPrefix || 'Error:',
    successMessage: formText?.successMessage || 'Booking submitted successfully!',
    cancelButton: formText?.cancelButton || 'Cancel',
    confirmButton: formText?.confirmButton || 'Confirm Booking',
    processingText: formText?.processingText || 'Processing...',
  };

  const handleSubmit = async () => {
    // Reset states
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      // Get UTM tags from localStorage
      const utmTags = JSON.parse(localStorage.getItem('utmTags') || '{}') || {};

      // Find the selected exam object that contains id, name and price
      const selectedExamObject =
        customExamData && customExamData.length > 0
          ? {
              id: selectedExam,
              name: getCurrentExamName(),
              price: getCurrentPrice(),
            }
          : exams.find(exam => exam.id === selectedExam);

      const completeFormData = {
        ...formData,
        ...utmTags,
        examType: selectedExamObject?.name || getCurrentExamName(),
        examLevel: customExamData && customExamData.length > 0 ? selectedLevel : null,
        price: getCurrentPrice(),
      };

      const response = await fetch('https://ap-server-8qi1.onrender.com/leads/exam', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeFormData),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      setSubmitSuccess(true);
      setIsModalOpen(false);
      setFormData({ name: '', phone: '', email: '', preferredCommunicator: '' });
    } catch (error) {
      console.error('Error submitting booking:', error);
      setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div
      className="flex items-center justify-center p-4"
      style={{
        background: customBackgroundColor || undefined,
        color: customTextColor || 'inherit',
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `,
        }}
      />
      <div className="max-w-6xl mx-auto bg-white bg-opacity-95 rounded-lg shadow-2xl overflow-hidden backdrop-blur-sm flex-grow md:flex-grow-0">
        {customExamData && customExamData.length > 0 ? (
          /* New table structure with levels */
          <div className="overflow-x-auto">
            <div>
              {/* Mobile level selector */}
              <div className="px-4 py-4 md:hidden">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {resolvedFormText.levelLabel}
                </label>
                <div className="relative">
                  <select
                    value={selectedLevel}
                    onChange={e => setSelectedLevel(e.target.value)}
                    className="w-full text-gray-700 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none bg-white"
                  >
                    {levels.map(level => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Header with levels (desktop/tablet) */}
              <div className="hidden md:grid grid-cols-8 bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-600">
                <div className="px-4 py-4 font-semibold text-white col-span-2 min-w-48">
                  {examTypeText}
                </div>
                {levels.map(level => (
                  <div
                    key={level}
                    className="px-2 py-4 font-semibold text-white text-center min-w-20"
                  >
                    {level}
                  </div>
                ))}
              </div>

              {/* Exam Rows with pricing for each level (desktop/tablet) */}
              {examData.map((exam, examIndex) => (
                <div
                  key={examIndex}
                  className="hidden md:grid grid-cols-8 border-b border-gray-200 hover:bg-teal-50 transition-colors"
                >
                  <div className="px-4 py-4 text-sm text-gray-800 col-span-2 flex items-center min-w-48">
                    {exam.examType}
                  </div>
                  {levels.map(level => (
                    <div
                      key={level}
                      className="flex justify-center px-2 py-4 text-center min-w-20 "
                    >
                      <div
                        className={`w-16 h-12 rounded-lg border-2 cursor-pointer transition-all flex flex-col items-center justify-center ${
                          selectedExam === examIndex.toString() && selectedLevel === level
                            ? 'border-teal-600 bg-teal-100 text-teal-800'
                            : 'border-gray-400 bg-gray-200 hover:border-teal-400 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          setSelectedExam(examIndex.toString());
                          setSelectedLevel(level);
                        }}
                      >
                        <div className="text-xs font-bold text-black">
                          €{exam[level as keyof ExamData]}
                        </div>
                        {selectedExam === examIndex.toString() && selectedLevel === level && (
                          <div className="w-2 h-2 rounded-full bg-teal-600 mt-1"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Mobile rows with selected level only */}
              <div className="md:hidden flex flex-col">
                <div className="flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-600">
                  <div className="px-4 py-3 font-semibold text-white">{examTypeText}</div>
                  <div className="px-4 py-3 font-semibold text-white w-32 text-center">€</div>
                </div>
                {examData.map((exam, examIndex) => (
                  <div
                    key={examIndex}
                    className="flex items-center justify-between border-b border-gray-200 hover:bg-teal-50 transition-colors"
                  >
                    <div className="px-4 py-4 text-sm text-gray-800 flex-1 min-w-0">
                      {exam.examType}
                    </div>
                    <div className="px-4 py-4">
                      <button
                        type="button"
                        className={`w-24 h-10 rounded-lg border-2 cursor-pointer transition-all flex items-center justify-center ${
                          selectedExam === examIndex.toString()
                            ? 'border-teal-600 bg-teal-100 text-teal-800'
                            : 'border-gray-400 bg-gray-200 hover:border-teal-400 hover:bg-gray-100'
                        }`}
                        onClick={() => setSelectedExam(examIndex.toString())}
                      >
                        <span className="text-sm font-bold text-black">
                          €{exam[selectedLevel as keyof ExamData]}
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Legacy table structure for backwards compatibility */
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {/* Header */}
              <div className="grid grid-cols-3 bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-600">
                <div className="px-6 py-4 font-semibold text-white min-w-48">Exam</div>
                <div className="px-6 py-4 font-semibold text-white min-w-32">Price in EUR</div>
                <div className="px-6 py-4 min-w-20"></div>
              </div>

              {/* Exam Rows */}
              {exams.map(exam => (
                <div
                  key={exam.id}
                  className="grid grid-cols-3 border-b border-gray-200 hover:bg-teal-50 transition-colors"
                >
                  <div className="px-6 py-6 text-lg text-gray-800 min-w-48">{exam.name}</div>
                  <div className="px-6 py-6 text-2xl font-bold text-gray-900 min-w-32">
                    {exam.price}
                  </div>
                  <div className="px-6 py-6 flex justify-end min-w-20">
                    <div
                      className={`w-6 h-6 rounded-full border-2 cursor-pointer transition-all ${
                        selectedExam === exam.id
                          ? 'border-teal-600 bg-teal-600'
                          : 'border-gray-400 hover:border-teal-400'
                      }`}
                      onClick={() => setSelectedExam(exam.id)}
                    >
                      {selectedExam === exam.id && (
                        <div className="w-full h-full rounded-full bg-teal-600 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-6 bg-gradient-to-r from-gray-100 to-gray-50 flex justify-end items-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-bold py-3 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg border-2 border-white"
          >
            {buttonText}
          </button>
        </div>
      </div>
      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          onClick={() => setIsModalOpen(false)}
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(15,100,91,0.9) 100%)',
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          <div
            className="bg-white bg-opacity-95 backdrop-blur-sm rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl border border-teal-200"
            style={{
              animation: 'slideIn 0.3s ease-out',
            }}
            onClick={e => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-700">{resolvedFormText.title}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="bg-teal-50 border border-teal-100 rounded-md p-3 mb-4">
              <div className="flex justify-between items-center">
                <div className="text-teal-800">
                  {resolvedFormText.selectedLabel}:{' '}
                  <span className="font-medium">{getCurrentExamName()}</span>
                  {customExamData && customExamData.length > 0 && (
                    <span className="text-sm ml-2 text-teal-600">
                      {resolvedFormText.levelLabel}: {selectedLevel}
                    </span>
                  )}
                </div>
                <div className="text-teal-800 font-bold ml-2">{getCurrentPrice()}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="flex text-sm font-medium text-gray-700 mb-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1 text-teal-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {resolvedFormText.nameLabel}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={resolvedFormText.namePlaceholder}
                    className="w-full text-gray-700 px-3 py-2 pl-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex text-sm font-medium text-gray-700 mb-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1 text-teal-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  {resolvedFormText.phoneLabel}
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={resolvedFormText.phonePlaceholder}
                    className="w-full text-gray-700 px-3 py-2 pl-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex text-sm font-medium text-gray-700 mb-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1 text-teal-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  {resolvedFormText.emailLabel}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={resolvedFormText.emailPlaceholder}
                    className="w-full text-gray-700 px-3 py-2 pl-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex text-sm font-medium text-gray-700 mb-1 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1 text-teal-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {resolvedFormText.preferredCommunicationLabel}
                </label>
                <div className="relative">
                  <select
                    name="preferredCommunicator"
                    value={formData.preferredCommunicator}
                    onChange={handleInputChange}
                    className="w-full text-gray-700 px-3 py-2 pl-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all appearance-none"
                  >
                    <option value="">{resolvedFormText.preferredCommunicationPlaceholder}</option>
                    <option value="viber">Viber</option>
                    <option value="telegram">Telegram</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="other">{resolvedFormText.preferredCommunicationOther}</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  <p className="text-sm">
                    {resolvedFormText.errorPrefix} {submitError}
                  </p>
                </div>
              )}

              {submitSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">
                  <p className="text-sm">{resolvedFormText.successMessage}</p>
                </div>
              )}

              <div className="flex justify-between pt-6 items-center">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium flex items-center disabled:opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {resolvedFormText.cancelButton}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-md hover:from-teal-700 hover:to-teal-800 transition-all border border-teal-400 shadow-lg flex items-center disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {resolvedFormText.processingText}
                    </>
                  ) : (
                    <>
                      {resolvedFormText.confirmButton}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamBookingTable;
